import React, { useState, useEffect } from 'react';
import { Row, Col, Progress, Alert, Spin, Typography } from 'antd';
import {
    LoadingOutlined,
    EyeOutlined,
    UserAddOutlined,
    UserOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    StarOutlined,
    StarFilled,
    MessageOutlined
} from '@ant-design/icons';
import api from '../api/axios';



const { Title, Text } = Typography;

const Counters = ({ profileId }) => {
    const [dashboardData, setDashboardData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [profileScore, setProfileScore] = useState(0);
    const [missingFields, setMissingFields] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const response = await api.get('/profiles/dashboard', {
                    params: {
                        profile_id: profileId,
                    },
                });
                setDashboardData(response.data.data);
                setLoading(false);
            } catch (error) {
                setError('Error fetching dashboard data');
                setLoading(false);
            }
        };

        const fetchProfileScore = async () => {
            try {
                const response = await api.post(
                    '/profiles/profile-score',
                    { profile_id: profileId }
                );

                if (response.status === 200) {
                    setProfileScore(Math.round(response.data.data.completion_percentage));
                    setMissingFields(response.data.data.missing_fields ?? []);
                }
            } catch (error) {
                console.error('Error fetching profile score:', error);
            }
        };

        fetchDashboardData();
        fetchProfileScore();
    }, [profileId]);

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: `calc(100vh - 100px)`,
                }}
            >
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48, color: '#AD1457' }} spin />} />
            </div>
        );
    }

    if (error) {
        return <div>{error}</div>;
    }

    const counterCards = [
        {
            title: 'Profile Views',
            value: dashboardData.profile_views,
            icon: <EyeOutlined />,
            gradient: 'linear-gradient(135deg, #F59E0B, #FCD34D)',
            iconBg: 'rgba(255,255,255,0.25)',
        },
        {
            title: 'Received Requests',
            value: dashboardData.received_invites,
            icon: <UserAddOutlined />,
            gradient: 'linear-gradient(135deg, #880E6E, #AD1457)',
            iconBg: 'rgba(255,255,255,0.2)',
        },
        {
            title: 'Sent Requests',
            value: dashboardData.sent_invites,
            icon: <UserOutlined />,
            gradient: 'linear-gradient(135deg, #AD1457, #D81B60)',
            iconBg: 'rgba(255,255,255,0.2)',
        },
        {
            title: 'Accepted Requests',
            value: dashboardData.accepted_requests,
            icon: <CheckCircleOutlined />,
            gradient: 'linear-gradient(135deg, #059669, #10B981)',
            iconBg: 'rgba(255,255,255,0.2)',
        },
        {
            title: 'Rejected Requests',
            value: dashboardData.rejected_requests,
            icon: <CloseCircleOutlined />,
            gradient: 'linear-gradient(135deg, #DC2626, #EF4444)',
            iconBg: 'rgba(255,255,255,0.2)',
        },
        {
            title: 'Shortlisted by Others',
            value: dashboardData.shortlisted_by_others,
            icon: <StarFilled />,
            gradient: 'linear-gradient(135deg, #D97706, #F59E0B)',
            iconBg: 'rgba(255,255,255,0.2)',
        },
        {
            title: 'Shortlisted by Me',
            value: dashboardData.shortlisted_by_me,
            icon: <StarOutlined />,
            gradient: 'linear-gradient(135deg, #6A0572, #880E6E)',
            iconBg: 'rgba(255,255,255,0.2)',
        },
        {
            title: 'Unread Messages',
            value: dashboardData.unread_messages,
            icon: <MessageOutlined />,
            gradient: 'linear-gradient(135deg, #0369A1, #0891B2)',
            iconBg: 'rgba(255,255,255,0.2)',
        },
    ];

    return (
        <div style={{ padding: '8px 0 24px' }}>
            {dashboardData.success && (
                <Alert message={dashboardData.success} type="success" showIcon closable style={{ marginBottom: '20px', borderRadius: '10px' }} />
            )}
            {dashboardData.error && (
                <Alert message={dashboardData.error} type="error" showIcon closable style={{ marginBottom: '20px', borderRadius: '10px' }} />
            )}

            {/* Profile Completion */}
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px 28px',
                marginBottom: '24px',
                border: '1px solid rgba(173,20,87,0.08)',
                boxShadow: '0 4px 20px rgba(173,20,87,0.06)',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <div>
                        <Title level={5} style={{ margin: 0, color: '#1A1A2E', fontWeight: '700' }}>
                            Profile Completion
                        </Title>
                        <Text style={{ color: '#64748B', fontSize: '0.85rem' }}>
                            Complete your profile to get more accurate matches
                        </Text>
                    </div>
                    <div style={{
                        fontSize: '1.5rem', fontWeight: '800',
                        color: profileScore >= 80 ? '#10B981' : profileScore >= 50 ? '#F59E0B' : '#AD1457',
                    }}>
                        {profileScore}%
                    </div>
                </div>
                <Progress
                    percent={profileScore}
                    showInfo={false}
                    strokeColor={{ '0%': '#AD1457', '100%': '#D81B60' }}
                    trailColor='rgba(173,20,87,0.1)'
                    strokeWidth={8}
                    style={{ marginBottom: missingFields.length > 0 ? '16px' : 0 }}
                />
                {missingFields.length > 0 && (
                    <div>
                        <Text style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                            Add the following to reach 100%:
                        </Text>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                            {missingFields.map(field => (
                                <span key={field} style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                    background: 'rgba(173,20,87,0.06)',
                                    border: '1px solid rgba(173,20,87,0.18)',
                                    color: '#AD1457',
                                    fontSize: '0.75rem', fontWeight: '600',
                                    padding: '3px 10px', borderRadius: '20px',
                                }}>
                                    <CloseCircleOutlined style={{ fontSize: '10px' }} />
                                    {field}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Counter Cards */}
            <Row gutter={[16, 16]}>
                {counterCards.map((card, index) => (
                    <Col key={index} xs={12} sm={12} md={8} lg={6}>
                        <div style={{
                            background: card.gradient,
                            borderRadius: '16px',
                            padding: '20px',
                            height: '100%',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                            transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                            cursor: 'default',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 10px 32px rgba(0,0,0,0.2)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            {/* Decorative circle */}
                            <div style={{
                                position: 'absolute', top: '-20px', right: '-20px',
                                width: '80px', height: '80px', borderRadius: '50%',
                                background: 'rgba(255,255,255,0.1)',
                                pointerEvents: 'none',
                            }} />
                            <div style={{
                                position: 'absolute', bottom: '-30px', left: '-10px',
                                width: '90px', height: '90px', borderRadius: '50%',
                                background: 'rgba(255,255,255,0.06)',
                                pointerEvents: 'none',
                            }} />

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{
                                    width: '42px', height: '42px', borderRadius: '12px',
                                    background: card.iconBg,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    marginBottom: '14px',
                                    fontSize: '20px', color: 'white',
                                    backdropFilter: 'blur(4px)',
                                }}>
                                    {card.icon}
                                </div>
                                <div style={{ fontSize: '2rem', fontWeight: '800', color: 'white', lineHeight: 1, marginBottom: '6px' }}>
                                    {card.value ?? 0}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', fontWeight: '500' }}>
                                    {card.title}
                                </div>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
        </div>
    );
};

export default Counters;
